import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Line, LineChart, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { api } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { themedHex } from "@/lib/format";

export default function LaneTrends() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const stroke = themedHex("#22D3EE", theme);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/trends", { params: { weeks: 8 } }).then((r) => setData(r.data)); }, []);
  if (!data) return null;

  const goWeek = (laneKey, idx) => {
    const ws = data.week_starts?.[idx];
    if (ws) navigate(`/referrals?lane=${encodeURIComponent(laneKey)}&week=${ws}`);
  };

  return (
    <div className="glass rounded-xl p-4" data-testid="lane-trends">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-head font-bold text-sm uppercase tracking-[0.18em] text-muted-foreground">Lane Trends — last {data.weeks.length} weeks</h2>
        <span className="text-[11px] font-mono text-muted-foreground">click a point to drill into that week · click a card for the lane</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.lanes.map((l) => {
          const rates = l.completed_rate.filter((x) => x != null);
          const latestRate = rates.length ? rates[rates.length - 1] : null;
          const chartData = l.volume.map((v, i) => ({ i, v }));
          return (
            <div
              key={l.key}
              className="rounded-lg border border-white/8 bg-slate-900/40 p-2.5 cursor-pointer row-hover"
              onClick={() => navigate(`/referrals?lane=${encodeURIComponent(l.key)}`)}
              data-testid={`trend-${l.key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium truncate">{l.key}</span>
                <span className="font-mono text-xs text-cyan-300 tnum">{l.total}</span>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <ResponsiveContainer width="100%" height={34}>
                  <LineChart
                    data={chartData}
                    margin={{ top: 3, right: 2, bottom: 0, left: 2 }}
                    onClick={(st) => { if (st && st.activeTooltipIndex != null) goWeek(l.key, st.activeTooltipIndex); }}
                    style={{ cursor: "pointer" }}
                  >
                    <YAxis hide domain={[0, "dataMax + 1"]} />
                    <Tooltip cursor={{ stroke: stroke, strokeWidth: 1 }} contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11, fontFamily: "JetBrains Mono, monospace", padding: "2px 8px" }} labelFormatter={(i) => `wk ${data.weeks[i]}`} formatter={(v) => [v, "referrals"]} />
                    <Line type="monotone" dataKey="v" stroke={stroke} strokeWidth={1.6} dot={{ r: 2, fill: stroke }} activeDot={{ r: 4 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-mono">Compl.</span>
                <span className="font-mono text-xs tnum" style={{ color: latestRate == null ? themedHex("#64748B", theme) : latestRate >= 50 ? themedHex("#34D399", theme) : themedHex("#EAB308", theme) }}>
                  {latestRate == null ? "—" : `${latestRate}%`}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
